import React, { useState, useEffect } from 'react';
import { X, CheckCircle, AlertTriangle, Clock, FileText, Zap, GraduationCap, ChevronDown, ChevronUp, Edit3 } from 'lucide-react';
import { getChecklistIcon } from '../config/emergencyChecklists';
import { useChecklistTimer } from '../hooks/useChecklistTimer';
import { exportChecklistToText } from '../utils/checklistExport';
import { 
  saveChecklistProgress, 
  loadChecklistProgress, 
  clearChecklistProgress, 
  saveChecklistCompletion, 
  logStepCompletion 
} from '../utils/checklistStorage';

/**
 * EmergencyChecklistModal - Display and track emergency checklist progress
 * Reduced size by extracting timer logic, export, and storage utilities
 */
const EmergencyChecklistModal = ({ isOpen, onClose, checklist }) => {
  const [completedSteps, setCompletedSteps] = useState(new Set());
  const [stepTimestamps, setStepTimestamps] = useState({});
  const [startTime, setStartTime] = useState(null);
  const [notes, setNotes] = useState('');
  const [isComplete, setIsComplete] = useState(false);
  const [isDrillMode, setIsDrillMode] = useState(false);
  const [notesExpanded, setNotesExpanded] = useState(false);
  
  // Use custom timer hook
  const { elapsedTime, formatTime } = useChecklistTimer(isOpen, startTime);

  // Load saved progress when modal opens
  useEffect(() => {
    if (!isOpen || !checklist) return;
    
    const progress = loadChecklistProgress(checklist.id);
    if (progress) {
      setCompletedSteps(new Set(progress.completedSteps || []));
      setStepTimestamps(progress.stepTimestamps || {});
      setNotes(progress.notes || '');
      setIsDrillMode(progress.isDrillMode || false);
      // Resume from saved time
      const savedStartTime = new Date(progress.startTime);
      setStartTime(savedStartTime);
    } else {
      // New checklist
      setStartTime(new Date());
    }
  }, [isOpen, checklist?.id]); // Only depend on checklist.id to avoid re-runs

  // Check if all required steps are complete
  useEffect(() => {
    if (checklist && checklist.steps) {
      const requiredSteps = checklist.steps.filter(step => step.required !== false);
      const allRequiredComplete = requiredSteps.every(step => 
        completedSteps.has(step.id || step.text)
      );
      setIsComplete(allRequiredComplete);
    }
  }, [completedSteps, checklist]);

  if (!isOpen || !checklist) return null;

  const Icon = getChecklistIcon(checklist.type);

  // Save progress wrapper
  const saveProgress = () => {
    saveChecklistProgress(checklist, completedSteps, stepTimestamps, notes, isDrillMode, startTime);
  };

  // Toggle step completion with timestamp
  const toggleStep = (stepId) => {
    const newCompleted = new Set(completedSteps);
    const newTimestamps = { ...stepTimestamps };
    const timestamp = new Date();
    
    if (newCompleted.has(stepId)) {
      newCompleted.delete(stepId);
      delete newTimestamps[stepId];
    } else {
      newCompleted.add(stepId);
      newTimestamps[stepId] = timestamp.toISOString(); // Store as ISO string
    }
    
    setCompletedSteps(newCompleted);
    setStepTimestamps(newTimestamps);
    
    // Log the step completion
    logStepCompletion(checklist, stepId, newCompleted.has(stepId));
    
    // Save progress after each step
    saveProgress();
  };

  // Save checklist completion
  const handleComplete = () => {
    saveChecklistCompletion(checklist, completedSteps, stepTimestamps, notes, isDrillMode, startTime, elapsedTime, 'completed');
    clearChecklistProgress(checklist.id);
    
    // Reset state
    setCompletedSteps(new Set());
    setStepTimestamps({});
    setNotes('');
    setStartTime(null);
    setIsDrillMode(false);
    setNotesExpanded(false);
    
    // Close modal
    onClose();
  };

  // Export checklist as text
  const handleExport = () => {
    exportChecklistToText(checklist, completedSteps, stepTimestamps, notes, isDrillMode, elapsedTime);
  };

  // Handle close and save progress
  const handleClose = () => {
    saveProgress();
    onClose();
  };

  // Handle cancel/abort - clear progress and close
  const handleCancel = () => {
    if (window.confirm('Are you sure you want to cancel this checklist? All progress will be lost.')) {
      clearChecklistProgress(checklist.id);
      
      // Reset state
      setCompletedSteps(new Set());
      setStepTimestamps({});
      setNotes('');
      setStartTime(null);
      setIsDrillMode(false);
      setNotesExpanded(false);
      
      // Close modal
      onClose();
    }
  };

  // Handle end session - save as incomplete
  const handleEndSession = () => {
    saveChecklistCompletion(checklist, completedSteps, stepTimestamps, notes, isDrillMode, startTime, elapsedTime, 'incomplete');
    clearChecklistProgress(checklist.id);
    
    // Reset state
    setCompletedSteps(new Set());
    setStepTimestamps({});
    setNotes('');
    setStartTime(null);
    setIsDrillMode(false);
    setNotesExpanded(false);
    
    // Close modal
    onClose();
  };

  return (
    <div className="emergency-checklist-modal-overlay" onClick={handleClose}>
      <div className="emergency-checklist-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="modal-header">
          <div className="header-left">
            <Icon size={28} />
            <div>
              <h2>{checklist.title}</h2>
              {checklist.reference && (
                <span className="reference-text">Ref: {checklist.reference}</span>
              )}
            </div>
          </div>
          <div className="header-right">
            <div className="timer">
              <Clock size={18} />
              <span>{formatTime(elapsedTime)}</span>
            </div>
            <button onClick={handleClose} className="close-button">
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Drill Mode Toggle */}
        <div className="drill-mode-bar">
          <button
            className={`drill-mode-toggle ${isDrillMode ? 'active' : ''}`}
            onClick={() => setIsDrillMode(!isDrillMode)}
          >
            {isDrillMode ? (
              <>
                <GraduationCap size={18} />
                <span>DRILL MODE - Not recorded in logbook</span>
              </>
            ) : (
              <>
                <Zap size={18} />
                <span>REAL EMERGENCY - Will be recorded in logbook</span>
              </>
            )}
          </button>
          {isDrillMode && (
            <div className="drill-mode-info">
              Practice mode for training - No logbook entry will be created
            </div>
          )}
        </div>

        {/* Priority Badge */}
        {checklist.priority && (
          <div className={`priority-badge priority-${checklist.priority}`}>
            {checklist.priority.toUpperCase()} PRIORITY
          </div>
        )}

        {/* Notes/Warnings */}
        {(checklist.notes || checklist.disclaimer) && (
          <div className="checklist-notes">
            <AlertTriangle size={18} />
            <p>{checklist.notes || checklist.disclaimer}</p>
          </div>
        )}

        {/* Progress Bar */}
        <div className="progress-section">
          <div className="progress-bar">
            <div 
              className="progress-fill"
              style={{ width: `${(completedSteps.size / checklist.steps.length) * 100}%` }}
            />
          </div>
          <span className="progress-text">
            {completedSteps.size} of {checklist.steps.length} steps complete
          </span>
        </div>

        {/* Checklist Steps */}
        <div className="checklist-steps">
          {checklist.steps.map((step, index) => {
            const stepId = step.id || step.text;
            const isCompleted = completedSteps.has(stepId);
            const isRequired = step.required !== false;
            const isCritical = step.critical === true;
            
            const timestamp = stepTimestamps[stepId];
            
            return (
              <div 
                key={stepId}
                className={`checklist-step ${isCompleted ? 'completed' : ''} ${isCritical ? 'critical' : ''}`}
              >
                <input
                  type="checkbox"
                  id={`step-${index}`}
                  checked={isCompleted}
                  onChange={() => toggleStep(stepId)}
                  className="step-checkbox"
                />
                <label htmlFor={`step-${index}`} className="step-label">
                  <span className="step-number">{index + 1}.</span>
                  <div className="step-content">
                    <span className="step-text">
                      {step.text}
                      {isCritical && <span className="critical-badge">CRITICAL</span>}
                      {!isRequired && <span className="optional-badge">Optional</span>}
                    </span>
                    {timestamp && (
                      <span className="step-timestamp">
                        Completed at {new Date(timestamp).toLocaleTimeString()}
                      </span>
                    )}
                  </div>
                </label>
                {isCompleted && (
                  <CheckCircle size={20} className="completed-icon" />
                )}
              </div>
            );
          })}
        </div>

        {/* Notes Section - Collapsible */}
        <div className="notes-section">
          <div className="notes-header">
            <button
              type="button"
              className="notes-toggle"
              onClick={() => setNotesExpanded(!notesExpanded)}
            >
              {notesExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              <Edit3 size={16} />
              <span>Additional Notes</span>
              {notes && !notesExpanded && (
                <span className="notes-preview">{notes.substring(0, 30)}...</span>
              )}
            </button>
          </div>
          {notesExpanded && (
            <textarea
              id="emergency-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Document any important details, deviations, or outcomes..."
              rows={4}
              className="notes-textarea"
              autoFocus
            />
          )}
        </div>

        {/* Action Buttons */}
        <div className="modal-actions">
          <button onClick={handleCancel} className="btn-danger">
            <X size={18} />
            Cancel
          </button>
          <button onClick={handleEndSession} className="btn-warning">
            <Clock size={18} />
            End Session
          </button>
          <button onClick={handleExport} className="btn-secondary">
            <FileText size={18} />
            Export Record
          </button>
          <button 
            onClick={handleComplete} 
            className={`btn-primary ${!isComplete ? 'disabled' : ''}`}
            disabled={!isComplete}
          >
            {isComplete ? 'Complete & Close' : 'Complete Required Steps'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmergencyChecklistModal;